import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ApplyQuoteTemplateInput,
  CreateQuoteTemplateInput,
  OpportunityInlineImage,
  PaginatedResult,
  PublicQuoteView,
  Quote,
  QuoteNotificationSettings,
  QuoteTemplate,
  QuoteVisit,
  ResolveInlineImagesResult,
  SendQuoteEmailInput,
  UpdateQuoteInput,
  UpdateQuoteNotificationSettingsInput,
  UpdateQuoteTemplateInput,
} from '@catalyst-psa/shared';

import { api, type DownloadResult } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface QuoteListParams {
  page: number;
  pageSize: number;
  opportunityId?: string;
  latestOnly?: boolean;
}

const quoteKeys = {
  all: ['quotes'] as const,
  list: (params: QuoteListParams) => ['quotes', 'list', params] as const,
  detail: (id: string) => ['quotes', 'detail', id] as const,
  templates: ['quote-templates'] as const,
  template: (id: string) => ['quote-templates', 'detail', id] as const,
  notifications: ['quote-notifications'] as const,
};

export function useQuotes(params: QuoteListParams): UseQueryResult<PaginatedResult<Quote>> {
  return useQuery({
    queryKey: quoteKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Quote>>(
        `/quotes${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          opportunityId: params.opportunityId,
          latestOnly: params.latestOnly,
        })}`,
      ),
  });
}

export function useQuote(id: string): UseQueryResult<Quote> {
  return useQuery({
    queryKey: quoteKeys.detail(id),
    enabled: Boolean(id),
    queryFn: () => api.get<Quote>(`/quotes/${id}`),
  });
}

/** Opens the rendered quote PDF in a new browser tab for preview. */
export function usePreviewQuotePdf(): UseMutationResult<DownloadResult, unknown, string> {
  return useMutation({
    mutationFn: (id: string) => api.download(`/quotes/${id}/pdf`),
    onSuccess: (result) => {
      const url = URL.createObjectURL(result.blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the opened tab has time to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
}

/** Renders the (possibly unsaved) template content to a preview PDF in a new tab. */
export function usePreviewQuoteTemplatePdf(): UseMutationResult<
  DownloadResult,
  unknown,
  UpdateQuoteTemplateInput
> {
  return useMutation({
    mutationFn: (input: UpdateQuoteTemplateInput) =>
      api.downloadPost('/quote-templates/preview-pdf', input),
    onSuccess: (result) => {
      const url = URL.createObjectURL(result.blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a delay so the opened tab has time to load the blob.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
}

export interface QuoteDeliveryPreviewArgs {
  id: string;
  /** The editor's current (possibly unsaved) sections/placements/frequencies. */
  input: UpdateQuoteInput;
}

/** Renders the quote's public delivery page model with unsaved edits (no visit recorded). */
export function useQuoteDeliveryPreview(): UseMutationResult<
  PublicQuoteView,
  unknown,
  QuoteDeliveryPreviewArgs
> {
  return useMutation({
    mutationFn: ({ id, input }: QuoteDeliveryPreviewArgs) =>
      api.post<PublicQuoteView>(`/quotes/${id}/delivery-preview`, input),
  });
}

/** Renders the (possibly unsaved) template content as a delivery-page preview model. */
export function usePreviewQuoteTemplateDeliveryView(): UseMutationResult<
  PublicQuoteView,
  unknown,
  UpdateQuoteTemplateInput
> {
  return useMutation({
    mutationFn: (input: UpdateQuoteTemplateInput) =>
      api.post<PublicQuoteView>('/quote-templates/preview-view', input),
  });
}

/** Uploads a picture pasted into a template section and returns its id + display URL. */
export function uploadQuoteTemplateImage(
  templateId: string,
  file: File,
): Promise<OpportunityInlineImage> {
  const form = new FormData();
  form.append('file', file);
  return api.post<OpportunityInlineImage>(`/quote-templates/${templateId}/images`, form);
}

/** Resolves template inline-image ids to fresh display URLs for rendering. */
export function resolveQuoteTemplateImages(
  templateId: string,
  ids: string[],
): Promise<ResolveInlineImagesResult> {
  return api.post<ResolveInlineImagesResult>(`/quote-templates/${templateId}/images/resolve`, {
    ids,
  });
}

/** Enables (or rotates) the public delivery link (/q/:token). */
export function useRegenerateQuoteDeliveryLink(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/quotes/${id}/delivery-link/regenerate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Disables the public delivery link. */
export function useDisableQuoteDeliveryLink(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/quotes/${id}/delivery-link/disable`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Enables (or rotates) the view-only "SOW for delivery team" link (/sow/:token). */
export function useRegenerateQuoteSowLink(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/quotes/${id}/sow-link/regenerate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Disables the view-only "SOW for delivery team" link. */
export function useDisableQuoteSowLink(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/quotes/${id}/sow-link/disable`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

export interface SendQuoteEmailArgs {
  id: string;
  input: SendQuoteEmailInput;
}

export interface ReorderQuoteLinesArgs {
  id: string;
  /** All of the quote's line ids, in the desired top-to-bottom order. */
  orderedIds: string[];
}

/** Reorders the quote's line snapshot (persists sortOrder). */
export function useReorderQuoteLines(): UseMutationResult<Quote, unknown, ReorderQuoteLinesArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, orderedIds }: ReorderQuoteLinesArgs) =>
      api.patch<Quote>(`/quotes/${id}/lines/reorder`, { orderedIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Emails the quote link to the customer and marks the quote 'sent'. */
export function useSendQuoteEmail(): UseMutationResult<Quote, unknown, SendQuoteEmailArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SendQuoteEmailArgs) =>
      api.post<Quote>(`/quotes/${id}/send`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Recent public delivery page visits. */
export function useQuoteVisits(id: string, enabled = true): UseQueryResult<QuoteVisit[]> {
  return useQuery({
    queryKey: [...quoteKeys.detail(id), 'visits'],
    enabled: Boolean(id) && enabled,
    queryFn: () => api.get<QuoteVisit[]>(`/quotes/${id}/visits`),
  });
}

/** Creates the next quote version for an opportunity (v1 or a copy of the latest). */
export function useCreateQuote(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opportunityId: string) => api.post<Quote>('/quotes', { opportunityId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

export interface UpdateQuoteArgs {
  id: string;
  input: UpdateQuoteInput;
}

export function useUpdateQuote(): UseMutationResult<Quote, unknown, UpdateQuoteArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateQuoteArgs) => api.patch<Quote>(`/quotes/${id}`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(quoteKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

/** Copies an old version's content into a new latest version. */
export function usePromoteQuote(): UseMutationResult<Quote, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Quote>(`/quotes/${id}/promote`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

export interface ApplyQuoteTemplateArgs {
  id: string;
  templateId: string;
  /** Which template parts to import; omitted = everything. */
  input?: ApplyQuoteTemplateInput;
}

export function useApplyQuoteTemplate(): UseMutationResult<
  Quote,
  unknown,
  ApplyQuoteTemplateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, templateId, input }: ApplyQuoteTemplateArgs) =>
      api.post<Quote>(`/quotes/${id}/apply-template/${templateId}`, input ?? {}),
    onSuccess: (data) => {
      queryClient.setQueryData(quoteKeys.detail(data.id), data);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
      // The template's lines were appended to the opportunity's products.
      void queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

export function useDeleteQuote(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/quotes/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Quote templates (reads sales-scoped; mutations admin-only)
// ---------------------------------------------------------------------------

export function useQuoteTemplates(enabled = true): UseQueryResult<QuoteTemplate[]> {
  return useQuery({
    queryKey: quoteKeys.templates,
    enabled,
    queryFn: () => api.get<QuoteTemplate[]>('/quote-templates'),
  });
}

export function useQuoteTemplate(id: string): UseQueryResult<QuoteTemplate> {
  return useQuery({
    queryKey: quoteKeys.template(id),
    enabled: Boolean(id),
    queryFn: () => api.get<QuoteTemplate>(`/quote-templates/${id}`),
  });
}

export function useCreateQuoteTemplate(): UseMutationResult<
  QuoteTemplate,
  unknown,
  CreateQuoteTemplateInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQuoteTemplateInput) =>
      api.post<QuoteTemplate>('/quote-templates', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.templates });
    },
  });
}

export interface UpdateQuoteTemplateArgs {
  id: string;
  input: UpdateQuoteTemplateInput;
}

export function useUpdateQuoteTemplate(): UseMutationResult<
  QuoteTemplate,
  unknown,
  UpdateQuoteTemplateArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateQuoteTemplateArgs) =>
      api.patch<QuoteTemplate>(`/quote-templates/${id}`, input),
    onSuccess: (data) => {
      queryClient.setQueryData(quoteKeys.template(data.id), data);
      void queryClient.invalidateQueries({ queryKey: quoteKeys.templates });
    },
  });
}

export function useDeleteQuoteTemplate(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/quote-templates/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: quoteKeys.templates });
    },
  });
}

/** The admin-only mailboxes notified when a quote is approved or viewed. */
export function useQuoteNotificationSettings(
  enabled = true,
): UseQueryResult<QuoteNotificationSettings> {
  return useQuery({
    queryKey: quoteKeys.notifications,
    enabled,
    queryFn: () => api.get<QuoteNotificationSettings>('/quote-notifications'),
  });
}

export function useUpdateQuoteNotificationSettings(): UseMutationResult<
  QuoteNotificationSettings,
  unknown,
  UpdateQuoteNotificationSettingsInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateQuoteNotificationSettingsInput) =>
      api.put<QuoteNotificationSettings>('/quote-notifications', input),
    onSuccess: (settings) => {
      queryClient.setQueryData(quoteKeys.notifications, settings);
    },
  });
}
